import Button from 'components/button/Button';
import { FormDateField } from 'components/form/FormDateField';
import { RadioButtons } from 'components/radiobuttons/radiobuttons';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAccountContext } from 'pages/account/AccountContext';
import styles from './monthlyStatements.module.scss';

import { BrandVisual } from 'components/brandVisual/BrandVisual';
import instance, { ApiResponse, isAxiosErrorHandled } from 'api';
import { endpoints } from 'endpoints.config';
import { SubPageLoader } from 'components/subPageLoader/SubPageLoader';
import { useTranslation } from 'react-i18next';
import { startOfMonth, sub } from 'date-fns';
import { Toast } from '../../helpers/toast';

type Props = { accountNumber: string };

type DownloadStatementForm = {
    accountNumber: string;
    date: Date;
};

const contentDispositionRegex = /.*?filename="?(.*?\.pdf)"?/; // for some reason the endpoint sometimes has content type filename:"file" and sometimes filename:file

export const MonthlyStatements = (props: Props) => {
    const { accountId, monthlyStatementsEnabled, setMonthlyStatementsEnabled, accountStartDate } =
        useAccountContext();
    const { t } = useTranslation('monthlyStatements');

    const handleChangeSettings = (setting: 'Enabled' | 'Disabled') => {
        if ((setting === 'Enabled') === monthlyStatementsEnabled) return;
        instance
            .post(endpoints.accounts.monthlyStatementSettings, {
                customerAssetAccountsId: accountId,
                bEnabled: setting === 'Enabled',
            })
            .then((res) => {
                setMonthlyStatementsEnabled(setting === 'Enabled');
            })
            .catch((err) => setMonthlyStatementsEnabled(setting !== 'Enabled'));
    };

    const handleDownloadStatement = (
        values: DownloadStatementForm,
        helpers: FormikHelpers<DownloadStatementForm>
    ) => {
        instance
            .get<BlobPart>(endpoints.accounts.downloadMonthlyStatement, {
                params: {
                    customerAssetAccountsId: values.accountNumber,
                    month: values.date.getMonth() + 1,
                    year: values.date.getFullYear(),
                },
                responseType: 'blob',
            })
            .then((res) => {
                const contentDisposition: string = res.headers['content-disposition'];
                const regexResult = contentDispositionRegex.exec(contentDisposition ?? '');
                const fileName = regexResult ? regexResult[1] : 'Account_Statement.pdf';
                const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
            })
            .catch(async (error) => {
                const isJsonResponse =
                    error.response.headers['content-type']?.includes('application/json');

                if (isJsonResponse && isAxiosErrorHandled(error)) {
                    // Convert from blob response to json response
                    const text = await (error.response.data as any).text();
                    const json: ApiResponse = JSON.parse(text);
                    if (
                        json.errors?.some(
                            (err) => err.messageCode === 'Year_Month_Before_Start_Date'
                        )
                    ) {
                        // Shouldn't reach this as we have FE min date set on picker
                        helpers.setFieldError(
                            'date',
                            t('invalidDateBeforeCreation', { ns: 'transactions' })
                        );
                    } else if (
                        json.errors?.some((err) => err.messageCode === 'Statement_Not_Yet_Ready')
                    ) {
                        // Shouldn't reach this as we have FE max date set on picker
                        helpers.setFieldError('date', t('statementNotAvailable', { ns: 'transactions' }));
                    }
                } else {
                    Toast.openErrorToast(t('unableToDownload', { ns: 'transactions' }));
                }
            })
            .finally(() => helpers.setSubmitting(false));
    };

    const startDate = sub(new Date(), {
        months: 1,
        days: 2,
        minutes: -new Date().getTimezoneOffset(), // cancels out TZ differences so we're always available from the 3rd UTC
    }); // you can only view statements from the 3rd of the following month

    const downloadInitialValues: DownloadStatementForm = {
        accountNumber: accountId,
        date: startDate,
    };
    const isLoading = monthlyStatementsEnabled === null;
    if (isLoading) {
        return <SubPageLoader message={t('loading', { ns: 'common' })} />;
    }
    const minDate = Date.parse(accountStartDate)
        ? startOfMonth(new Date(accountStartDate))
        : new Date('2020-01');
    return (
        <div className={styles.MonthlyStatementsPage}>
            <div className={styles.Content}>
                <Formik
                    initialValues={{
                        monthlyStatements: monthlyStatementsEnabled ? 'Enabled' : 'Disabled',
                    }}
                    onSubmit={() => {}}
                    enableReinitialize
                >
                    <RadioButtons
                        options={['Enabled', 'Disabled']}
                        fieldname={'monthlyStatements'}
                        label={t('receiveStatements')}
                        customOnChange={handleChangeSettings}
                    />
                </Formik>
                <Formik initialValues={downloadInitialValues} onSubmit={handleDownloadStatement}>
                    {({ isSubmitting }) => (
                        <Form>
                            <div className="FormBuilderComponent SideBySide">
                                <FormDateField
                                    field={'date'}
                                    label={t('downloadByPeriod')}
                                    datePickerProps={{
                                        dateFormat: 'MM/yyyy',
                                        showMonthYearPicker: true,
                                        maxDate: startDate, //stop the user selecting the current month
                                        minDate: minDate,
                                    }}
                                />
                                <Button variety="full" disabled={isSubmitting}>
                                    <span className="notranslate" translate="no">{t('downloadStatement')}</span>
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
            <BrandVisual imageClassName={styles.FillerImage} />
        </div>
    );
};
